/**
 * =====================================================================================
 * MÓDULO VUEX DE CATÁLOGOS - catalogos.js
 * =====================================================================================
 *
 * Este módulo gestiona todos los CATÁLOGOS del Ministerio de Hacienda (MH).
 * Los catálogos son listas de valores oficiales que se usan en los DTE.
 *
 * ¿QUÉ SON LOS CATÁLOGOS DEL MH?
 * ------------------------------
 * El Ministerio de Hacienda de El Salvador define catálogos oficiales para
 * estandarizar los valores usados en las facturas electrónicas.
 *
 * EJEMPLOS:
 * - CAT-012: Departamentos de El Salvador
 * - CAT-013: Municipios (dependen del departamento)
 * - CAT-017: Unidades de medida (unidad, docena, kilogramo...)
 * - CAT-024: Formas de pago (efectivo, tarjeta, cheque...)
 *
 * ¿POR QUÉ UN MÓDULO SEPARADO PARA CATÁLOGOS?
 * -------------------------------------------
 * 1. Los catálogos se usan en MUCHOS lugares de la aplicación
 * 2. No cambian frecuentemente - se pueden cachear
 * 3. Centralizarlos evita cargarlos múltiples veces
 *
 * USO EN COMPONENTES:
 * ------------------
 * Los componentes acceden a los catálogos vía getters:
 *
 * const departamentos = computed(() => store.getters['catalogos/departamentos'])
 *
 * Y luego los usan en v-select u otros componentes de selección.
 *
 * RELACIÓN CON OTROS ARCHIVOS:
 * ----------------------------
 * - src/services/catalogos.service.js: Hace las peticiones HTTP al backend
 * - src/components/emision/*: Usan catálogos para selectores en formularios
 * - src/views/emision/FacturaView.vue: Carga catálogos al montar
 * - Backend: Controllers/CatalogosController.cs
 *
 * =====================================================================================
 */

// Importamos el servicio que hace las peticiones HTTP de catálogos
import catalogosService from '@/services/catalogos.service'

/**
 * =====================================================================================
 * STATE (Estado del Módulo)
 * =====================================================================================
 *
 * Contiene arrays para cada tipo de catálogo.
 * Inicialmente están vacíos y se llenan al llamar fetchCatalogosBasicos.
 *
 * ESTRUCTURA DE UN ITEM DE CATÁLOGO:
 * {
 *   id: 1,            // ID en la base de datos
 *   codigo: "01",     // Código oficial del MH
 *   valor: "San Salvador"  // Descripción legible
 * }
 *
 * ALGUNOS CATÁLOGOS TIENEN ESTRUCTURA ESPECIAL:
 * - Municipios tienen codigoDepartamento para filtrar
 * - Actividades económicas tienen descripción larga
 */
const state = {
  // =================================================================================
  // CATÁLOGOS DE UBICACIÓN GEOGRÁFICA
  // =================================================================================
  /**
   * CAT-012: Departamentos de El Salvador
   * Ejemplo: { id: 1, codigo: "01", valor: "Ahuachapán" }
   */
  departamentos: [],

  /**
   * CAT-013: Municipios de El Salvador
   * Incluye codigoDepartamento para filtrar
   * Ejemplo: { id: 1, codigo: "01", valor: "Ahuachapán", codigoDepartamento: "01" }
   */
  municipios: [],

  /**
   * CAT-008: Distritos de El Salvador (Normativa V2.0)
   * Incluye codigoDepartamento y codigoMunicipio para la cascada
   * Ejemplo: { id: 2, codigo: "01", valor: "Ahuachapán", codigoDepartamento: "01", codigoMunicipio: "14" }
   */
  distritos: [],

  // =================================================================================
  // CATÁLOGOS ECONÓMICOS Y FISCALES
  // =================================================================================
  /**
   * CAT-019: Actividades económicas
   * Código CIIU de actividad comercial
   * Ejemplo: { id: 1, codigo: "10101", valor: "Cultivo de cereales" }
   */
  actividadesEconomicas: [],

  /**
   * CAT-015: Tributos
   * IVA, Impuesto especial, etc.
   * Ejemplo: { id: 1, codigo: "20", valor: "Impuesto al Valor Agregado 13%" }
   */
  tributos: [],

  // =================================================================================
  // CATÁLOGOS DE PRODUCTOS
  // =================================================================================
  /**
   * CAT-014: Unidades de medida
   * Unidad, docena, kilogramo, metro, etc.
   * Ejemplo: { id: 59, codigo: "59", valor: "Unidad" }
   */
  unidadesMedida: [],

  /**
   * CAT-011: Tipos de ítem
   * Bien o Servicio
   * Ejemplo: { id: 1, codigo: "1", valor: "Bien" }
   */
  tiposItem: [],

  // =================================================================================
  // CATÁLOGOS DE DOCUMENTOS
  // =================================================================================
  /**
   * CAT-002: Tipos de documento tributario
   * Factura, CCF, Nota de crédito, etc.
   * Ejemplo: { id: 1, codigo: "01", valor: "Factura" }
   */
  tiposDocumento: [],

  /**
   * CAT-022: Tipos de documento de identificación del receptor
   * NIT, DUI, Pasaporte, etc.
   * Ejemplo: { id: 36, codigo: "36", valor: "NIT" }
   */
  tiposDocumentoIdentificacionReceptor: [],

  /**
   * CAT-020: Tipos de generación de documento
   * Físico, Electrónico
   * Ejemplo: { id: 1, codigo: "1", valor: "Físico" }
   */
  tiposGeneracionDocumento: [],

  // =================================================================================
  // CATÁLOGOS DE PAGO
  // =================================================================================
  /**
   * CAT-017: Formas de pago
   * Efectivo, tarjeta, cheque, transferencia, etc.
   * Ejemplo: { id: 1, codigo: "01", valor: "Billetes y monedas" }
   */
  formasPago: [],

  /**
   * CAT-016: Condiciones de operación
   * Contado, crédito, otro
   * Ejemplo: { id: 1, codigo: "1", valor: "Contado" }
   */
  condicionesOperacion: [],

  /**
   * CAT-018: Plazos para créditos
   * Días, semanas, meses, años
   * Ejemplo: { id: 1, codigo: "01", valor: "Días" }
   */
  plazos: [],

  // =================================================================================
  // CATÁLOGOS DE ESTABLECIMIENTOS
  // =================================================================================
  /**
   * CAT-009: Tipos de establecimiento
   * Casa matriz, sucursal, agencia, etc.
   * Ejemplo: { id: 1, codigo: "01", valor: "Sucursal / Agencia" }
   */
  tiposEstablecimiento: [],

  // =================================================================================
  // CATÁLOGOS DE TRANSMISIÓN
  // =================================================================================
  /**
   * CAT-003: Tipos de transmisión
   * Normal, por contingencia
   * Ejemplo: { id: 1, codigo: "1", valor: "Transmisión normal" }
   */
  tiposTransmision: [],

  /**
   * CAT-004: Modelos de facturación
   * Previo, diferido
   * Ejemplo: { id: 1, codigo: "1", valor: "Modelo de facturación previo" }
   */
  modelosFacturacion: [],

  /**
   * CAT-001: Ambientes de destino
   * Pruebas, producción
   * Ejemplo: { id: 1, codigo: "00", valor: "Pruebas" }
   */
  ambientesDestino: [],

  // =================================================================================
  // CATÁLOGOS DE CONTINGENCIA
  // =================================================================================
  /**
   * CAT-005: Tipos de contingencia
   * Sin internet, servidor MH caído, etc.
   * Ejemplo: { id: 1, codigo: "1", valor: "No disponibilidad de sistema del MH" }
   */
  tiposContingencia: [],

  /**
   * CAT-006: Tipos de documento en contingencia
   * Similar a CAT-002 pero para contingencia
   */
  tiposDocumentoContingencia: [],

  // =================================================================================
  // CATÁLOGOS DE INVALIDACIÓN
  // =================================================================================
  /**
   * CAT-024: Tipos de invalidación
   * Error en datos, cambio de condiciones, etc.
   */
  tiposInvalidacion: [],

  // =================================================================================
  // OTROS CATÁLOGOS
  // =================================================================================
  /**
   * CAT-021: Otros documentos asociados
   * Para relacionar documentos de respaldo
   */
  otrosDocumentosAsociados: [],

  /**
   * CAT-025: Tipos de servicio
   * Para servicios específicos del MH
   */
  tiposServicio: [],

  // =================================================================================
  // FLAGS DE CONTROL
  // =================================================================================
  /**
   * Flag de carga en progreso
   * Se usa para mostrar spinners en la UI
   */
  loading: false,

  /**
   * Mensaje de error si falló la carga
   */
  error: null,

  /**
   * Flag que indica si los catálogos básicos ya fueron cargados
   * Evita recargar innecesariamente al navegar entre vistas
   */
  initialized: false
}

/**
 * =====================================================================================
 * GETTERS (Propiedades Computadas)
 * =====================================================================================
 *
 * Permiten acceder a los catálogos desde cualquier componente.
 *
 * USO EN COMPONENTES:
 * const departamentos = computed(() => store.getters['catalogos/departamentos'])
 *
 * O con mapGetters:
 * ...mapGetters('catalogos', ['departamentos', 'municipios'])
 */
const getters = {
  // Getters directos para cada catálogo
  departamentos: state => state.departamentos,
  municipios: state => state.municipios,
  distritos: state => state.distritos,
  actividadesEconomicas: state => state.actividadesEconomicas,
  tributos: state => state.tributos,
  unidadesMedida: state => state.unidadesMedida,
  tiposDocumento: state => state.tiposDocumento,
  formasPago: state => state.formasPago,
  condicionesOperacion: state => state.condicionesOperacion,
  tiposEstablecimiento: state => state.tiposEstablecimiento,
  tiposTransmision: state => state.tiposTransmision,
  modelosFacturacion: state => state.modelosFacturacion,
  ambientesDestino: state => state.ambientesDestino,
  tiposContingencia: state => state.tiposContingencia,
  tiposDocumentoContingencia: state => state.tiposDocumentoContingencia,
  tiposDocumentoIdentificacionReceptor: state => state.tiposDocumentoIdentificacionReceptor,
  tiposGeneracionDocumento: state => state.tiposGeneracionDocumento,
  tiposInvalidacion: state => state.tiposInvalidacion,
  tiposItem: state => state.tiposItem,
  plazos: state => state.plazos,
  otrosDocumentosAsociados: state => state.otrosDocumentosAsociados,
  tiposServicio: state => state.tiposServicio,
  loading: state => state.loading,
  error: state => state.error,
  initialized: state => state.initialized,

  /**
   * ===========================================================================
   * GETTER ESPECIAL: municipiosByDepartamento
   * ===========================================================================
   *
   * Este es un getter que retorna una FUNCIÓN.
   * Se usa cuando necesitas pasar parámetros al getter.
   *
   * SINTAXIS:
   * getter normal: state => state.algo
   * getter con parámetros: (state) => (parametro) => { ... }
   *
   * USO:
   * const municipiosAhuachapan = store.getters['catalogos/municipiosByDepartamento']('01')
   *
   * EJEMPLO EN COMPONENTE:
   * <v-select
   *   :items="getMunicipios(departamentoSeleccionado)"
   * />
   *
   * methods: {
   *   getMunicipios(codigoDept) {
   *     return this.$store.getters['catalogos/municipiosByDepartamento'](codigoDept)
   *   }
   * }
   *
   * @param {string} codigoDepartamento - Código del departamento (ej: "01")
   * @returns {Array} Municipios que pertenecen a ese departamento
   */
  municipiosByDepartamento: (state) => (codigoDepartamento) => {
    // Filtramos municipios donde codigoDepartamento coincida
    return state.municipios.filter(m => m.codigoDepartamento === codigoDepartamento)
  },

  /**
   * Distritos filtrados por departamento + municipio (cascada V2.0).
   * Uso: store.getters['catalogos/distritosByMunicipio']('06', '23')
   */
  distritosByMunicipio: (state) => (codigoDepartamento, codigoMunicipio) => {
    return state.distritos.filter(
      d => d.codigoDepartamento === codigoDepartamento && d.codigoMunicipio === codigoMunicipio
    )
  }
}

/**
 * =====================================================================================
 * MUTATIONS (Modificadores del Estado)
 * =====================================================================================
 *
 * Las mutations son las únicas que pueden modificar el state directamente.
 *
 * NOTA: Usamos una mutation genérica SET_CATALOGOS con { key, data }
 * en lugar de una mutation por cada catálogo. Esto reduce código repetitivo.
 */
const mutations = {
  /**
   * ===========================================================================
   * MUTATION: SET_CATALOGOS (Genérica)
   * ===========================================================================
   *
   * Mutation genérica que puede actualizar cualquier catálogo.
   * Recibe un objeto con key (nombre del catálogo) y data (los datos).
   *
   * SINTAXIS DE ACCESO DINÁMICO:
   * state[key] accede a una propiedad cuyo nombre está en la variable 'key'
   *
   * EJEMPLO:
   * commit('SET_CATALOGOS', { key: 'departamentos', data: [...] })
   * // Equivale a: state.departamentos = [...]
   *
   * commit('SET_CATALOGOS', { key: 'municipios', data: [...] })
   * // Equivale a: state.municipios = [...]
   *
   * @param {Object} state - Estado del módulo
   * @param {Object} payload - { key: string, data: Array }
   */
  SET_CATALOGOS(state, { key, data }) {
    state[key] = data
  },

  /**
   * Establece el flag de loading
   * @param {Object} state - Estado del módulo
   * @param {boolean} loading - true si está cargando
   */
  SET_LOADING(state, loading) {
    state.loading = loading
  },

  /**
   * Establece el mensaje de error
   * @param {Object} state - Estado del módulo
   * @param {string|null} error - Mensaje de error o null para limpiar
   */
  SET_ERROR(state, error) {
    state.error = error
  },

  /**
   * Marca si los catálogos ya fueron inicializados
   * @param {Object} state - Estado del módulo
   * @param {boolean} initialized - true si ya se cargaron
   */
  SET_INITIALIZED(state, initialized) {
    state.initialized = initialized
  }
}

/**
 * =====================================================================================
 * ACTIONS (Acciones Asíncronas)
 * =====================================================================================
 *
 * Las actions cargan los catálogos desde el backend.
 * Se dividen en:
 * - fetchCatalogosBasicos: Los más usados, se cargan al iniciar la app
 * - fetchCatalogosAdicionales: Se cargan bajo demanda
 * - fetchCatalogo: Carga un catálogo individual
 */
const actions = {
  /**
   * ===========================================================================
   * ACTION: fetchCatalogosBasicos
   * ===========================================================================
   *
   * Carga los catálogos más importantes al iniciar la aplicación.
   * Solo los catálogos necesarios para crear una factura básica.
   *
   * CATÁLOGOS BÁSICOS:
   * - Departamentos y municipios (dirección)
   * - Unidades de medida (productos)
   * - Formas de pago
   * - Condiciones de operación
   * - Tipos de documento
   * - Tributos
   * - Tipos de ítem
   *
   * OPTIMIZACIÓN - Promise.all:
   * --------------------------
   * Cargamos todos los catálogos EN PARALELO usando Promise.all.
   *
   * Sin Promise.all (secuencial - lento):
   *   const dept = await getDepartamentos()   // 200ms
   *   const muni = await getMunicipios()      // 200ms
   *   const unid = await getUnidadesMedida()  // 200ms
   *   // Total: 600ms
   *
   * Con Promise.all (paralelo - rápido):
   *   const [dept, muni, unid] = await Promise.all([
   *     getDepartamentos(),   // 200ms ┐
   *     getMunicipios(),      // 200ms ├─ Todos a la vez
   *     getUnidadesMedida()   // 200ms ┘
   *   ])
   *   // Total: ~200ms (el más lento)
   *
   * DESESTRUCTURACIÓN DE ARRAYS:
   * ---------------------------
   * const [a, b, c] = await Promise.all([promesa1, promesa2, promesa3])
   * a = resultado de promesa1
   * b = resultado de promesa2
   * c = resultado de promesa3
   *
   * @param {Object} context - { commit, state }
   */
  async fetchCatalogosBasicos({ commit, state }) {
    // GUARD CLAUSE: Si ya están inicializados, no recargar
    // Esto evita llamadas innecesarias al cambiar de vista
    if (state.initialized) return

    // Indicar que empezamos a cargar
    commit('SET_LOADING', true)
    commit('SET_ERROR', null)

    try {
      // Cargar todos los catálogos básicos EN PARALELO
      const [
        departamentos,
        municipios,
        distritos,
        unidadesMedida,
        formasPago,
        condicionesOperacion,
        tiposDocumento,
        tributos,
        tiposItem
      ] = await Promise.all([
        catalogosService.getDepartamentos(),
        catalogosService.getMunicipios(),
        catalogosService.getDistritos(),
        catalogosService.getUnidadesMedida(),
        catalogosService.getFormasPago(),
        catalogosService.getCondicionesOperacion(),
        catalogosService.getTiposDocumento(),
        catalogosService.getTributos(),
        catalogosService.getTiposItem()
      ])

      // Guardar cada catálogo en el state
      commit('SET_CATALOGOS', { key: 'departamentos', data: departamentos })
      commit('SET_CATALOGOS', { key: 'municipios', data: municipios })
      commit('SET_CATALOGOS', { key: 'distritos', data: distritos })
      commit('SET_CATALOGOS', { key: 'unidadesMedida', data: unidadesMedida })
      commit('SET_CATALOGOS', { key: 'formasPago', data: formasPago })
      commit('SET_CATALOGOS', { key: 'condicionesOperacion', data: condicionesOperacion })
      commit('SET_CATALOGOS', { key: 'tiposDocumento', data: tiposDocumento })
      commit('SET_CATALOGOS', { key: 'tributos', data: tributos })
      commit('SET_CATALOGOS', { key: 'tiposItem', data: tiposItem })

      // Marcar como inicializado para evitar recargas futuras
      commit('SET_INITIALIZED', true)
    } catch (error) {
      console.error('Error al cargar catalogos:', error)
      commit('SET_ERROR', 'No se pudieron cargar los catalogos. Verifique la conexion con el servidor.')
    } finally {
      // FINALLY siempre se ejecuta, haya error o no
      // Importante para quitar el loading
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: fetchCatalogosAdicionales
   * ===========================================================================
   *
   * Carga catálogos que no se necesitan al inicio pero sí en ciertas vistas.
   * Se llama "bajo demanda" cuando el usuario accede a configuración avanzada.
   *
   * CATÁLOGOS ADICIONALES:
   * - Actividades económicas (configuración de emisor)
   * - Tipos de establecimiento (sucursales)
   * - Tipos de transmisión/contingencia (operaciones especiales)
   * - Tipos de invalidación (anular documentos)
   * - etc.
   *
   * @param {Object} context - { commit }
   */
  async fetchCatalogosAdicionales({ commit }) {
    commit('SET_LOADING', true)
    try {
      // Cargar todos los catálogos adicionales EN PARALELO
      const [
        actividadesEconomicas,
        tiposEstablecimiento,
        tiposTransmision,
        modelosFacturacion,
        ambientesDestino,
        tiposContingencia,
        tiposDocumentoContingencia,
        tiposDocumentoIdentificacionReceptor,
        tiposGeneracionDocumento,
        tiposInvalidacion,
        plazos,
        otrosDocumentosAsociados,
        tiposServicio
      ] = await Promise.all([
        catalogosService.getActividadesEconomicas(),
        catalogosService.getTiposEstablecimiento(),
        catalogosService.getTiposTransmision(),
        catalogosService.getModelosFacturacion(),
        catalogosService.getAmbientesDestino(),
        catalogosService.getTiposContingencia(),
        catalogosService.getTiposDocumentoContingencia(),
        catalogosService.getTiposDocumentoIdentificacionReceptor(),
        catalogosService.getTiposGeneracionDocumento(),
        catalogosService.getTiposInvalidacion(),
        catalogosService.getPlazos(),
        catalogosService.getOtrosDocumentosAsociados(),
        catalogosService.getTiposServicio()
      ])

      // Guardar cada catálogo
      commit('SET_CATALOGOS', { key: 'actividadesEconomicas', data: actividadesEconomicas })
      commit('SET_CATALOGOS', { key: 'tiposEstablecimiento', data: tiposEstablecimiento })
      commit('SET_CATALOGOS', { key: 'tiposTransmision', data: tiposTransmision })
      commit('SET_CATALOGOS', { key: 'modelosFacturacion', data: modelosFacturacion })
      commit('SET_CATALOGOS', { key: 'ambientesDestino', data: ambientesDestino })
      commit('SET_CATALOGOS', { key: 'tiposContingencia', data: tiposContingencia })
      commit('SET_CATALOGOS', { key: 'tiposDocumentoContingencia', data: tiposDocumentoContingencia })
      commit('SET_CATALOGOS', { key: 'tiposDocumentoIdentificacionReceptor', data: tiposDocumentoIdentificacionReceptor })
      commit('SET_CATALOGOS', { key: 'tiposGeneracionDocumento', data: tiposGeneracionDocumento })
      commit('SET_CATALOGOS', { key: 'tiposInvalidacion', data: tiposInvalidacion })
      commit('SET_CATALOGOS', { key: 'plazos', data: plazos })
      commit('SET_CATALOGOS', { key: 'otrosDocumentosAsociados', data: otrosDocumentosAsociados })
      commit('SET_CATALOGOS', { key: 'tiposServicio', data: tiposServicio })
    } catch (error) {
      console.error('Error al cargar catalogos adicionales:', error)
      commit('SET_ERROR', 'No se pudieron cargar algunos catalogos.')
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * ===========================================================================
   * ACTION: fetchCatalogo (Individual)
   * ===========================================================================
   *
   * Carga un catálogo específico bajo demanda.
   * Útil cuando solo necesitas un catálogo en particular.
   *
   * PARÁMETROS:
   * - catalogo: Nombre de la propiedad en el state (ej: 'departamentos')
   * - serviceMethod: Función del servicio a llamar (ej: catalogosService.getDepartamentos)
   *
   * USO:
   * await dispatch('catalogos/fetchCatalogo', {
   *   catalogo: 'actividadesEconomicas',
   *   serviceMethod: catalogosService.getActividadesEconomicas
   * })
   *
   * @param {Object} context - { commit }
   * @param {Object} payload - { catalogo: string, serviceMethod: Function }
   * @returns {Array} Los datos del catálogo cargado
   */
  async fetchCatalogo({ commit }, { catalogo, serviceMethod }) {
    try {
      // Llamar al método del servicio que nos pasaron
      const data = await serviceMethod()
      // Guardar en el state
      commit('SET_CATALOGOS', { key: catalogo, data })
      // Retornar los datos por si el llamador los necesita
      return data
    } catch (error) {
      console.error(`Error al cargar catalogo ${catalogo}:`, error)
      // Re-lanzar el error para que el llamador pueda manejarlo
      throw error
    }
  },

  // Acciones individuales para cargar catálogos específicos
  async fetchActividadesEconomicas({ commit, state }) {
    if (state.actividadesEconomicas.length > 0) return state.actividadesEconomicas
    try {
      const rawData = await catalogosService.getActividadesEconomicas()
      // Normalizar los datos para asegurar campos consistentes
      // El backend puede devolver: codigo/Codigo/codigoActividad, valor/Valor/descripcion
      const data = (rawData || []).map(item => ({
        id: item.id || item.Id,
        codigo: item.codigo || item.Codigo || item.codigoActividad || item.CodigoActividad || '',
        valor: item.valor || item.Valor || item.descripcion || item.Descripcion || item.nombre || item.Nombre || ''
      }))
      commit('SET_CATALOGOS', { key: 'actividadesEconomicas', data })
      return data
    } catch (error) {
      console.error('Error al cargar actividades económicas:', error)
      throw error
    }
  },

  async fetchDepartamentos({ commit, state }) {
    if (state.departamentos.length > 0) return state.departamentos
    try {
      const data = await catalogosService.getDepartamentos()
      commit('SET_CATALOGOS', { key: 'departamentos', data })
      return data
    } catch (error) {
      console.error('Error al cargar departamentos:', error)
      throw error
    }
  },

  async fetchMunicipios({ commit, state }) {
    if (state.municipios.length > 0) return state.municipios
    try {
      const data = await catalogosService.getMunicipios()
      commit('SET_CATALOGOS', { key: 'municipios', data })
      return data
    } catch (error) {
      console.error('Error al cargar municipios:', error)
      throw error
    }
  },

  async fetchDistritos({ commit, state }) {
    if (state.distritos.length > 0) return state.distritos
    try {
      const data = await catalogosService.getDistritos()
      commit('SET_CATALOGOS', { key: 'distritos', data })
      return data
    } catch (error) {
      console.error('Error al cargar distritos:', error)
      throw error
    }
  },

  async fetchTiposEstablecimiento({ commit, state }) {
    if (state.tiposEstablecimiento.length > 0) return state.tiposEstablecimiento
    try {
      const data = await catalogosService.getTiposEstablecimiento()
      commit('SET_CATALOGOS', { key: 'tiposEstablecimiento', data })
      return data
    } catch (error) {
      console.error('Error al cargar tipos de establecimiento:', error)
      throw error
    }
  }
}

/**
 * =====================================================================================
 * EXPORTACIÓN DEL MÓDULO
 * =====================================================================================
 *
 * namespaced: true significa que todas las llamadas deben incluir 'catalogos/':
 * - store.getters['catalogos/departamentos']
 * - store.dispatch('catalogos/fetchCatalogosBasicos')
 *
 * FLUJO DE USO TÍPICO:
 * -------------------
 * 1. Al iniciar la app (App.vue mounted):
 *    await store.dispatch('catalogos/fetchCatalogosBasicos')
 *
 * 2. En componentes:
 *    const departamentos = computed(() => store.getters['catalogos/departamentos'])
 *
 * 3. En formularios con selects dependientes:
 *    const municipiosFiltrados = computed(() =>
 *      store.getters['catalogos/municipiosByDepartamento'](departamentoSeleccionado.value)
 *    )
 *
 * 4. Cargar catálogos adicionales cuando se necesiten:
 *    await store.dispatch('catalogos/fetchCatalogosAdicionales')
 */
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
